/* kalié — premium cart layer.
   Listens to the cart JSON that kalie.js / kalie-editorial-cart.js already fetch,
   then renders the rewards ladder, cure upsell, savings, totals and delivery estimate.
   It never replaces their logic: it only reads the cart and adds to it. */
(() => {
  'use strict';
  const money = cents => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format((cents || 0) / 100);
  const HEAD = 'la-tete-de-douche-kalie', CAPSULE = 'la-capsule-kdf-55';
  let cart = null, cfg = null, busy = false;

  /* ---------- delivery estimate (French business days) ---------- */
  function easter(y) {
    const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3),
      h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451),
      month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(Date.UTC(y, month - 1, day));
  }
  const key = d => d.toISOString().slice(0, 10);
  function holidays(y) {
    const e = easter(y), plus = n => key(new Date(e.getTime() + n * 864e5));
    return new Set([`${y}-01-01`, `${y}-05-01`, `${y}-05-08`, `${y}-07-14`, `${y}-08-15`, `${y}-11-01`, `${y}-11-11`, `${y}-12-25`, plus(1), plus(39), plus(50)]);
  }
  function parisToday() {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date()).map(x => [x.type, x.value]));
    return new Date(Date.UTC(+p.year, +p.month - 1, +p.day));
  }
  function addBusinessDays(start, n) {
    const d = new Date(start.getTime());
    let added = 0, cache = {};
    while (added < n) {
      d.setUTCDate(d.getUTCDate() + 1);
      const y = d.getUTCFullYear(), dow = d.getUTCDay();
      cache[y] = cache[y] || holidays(y);
      if (dow !== 0 && dow !== 6 && !cache[y].has(key(d))) added++;
    }
    return d;
  }
  const fmt = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  function etaText(min, max) {
    const today = parisToday();
    return `Livraison estimée en France : entre le <strong>${fmt.format(addBusinessDays(today, min))}</strong> et le <strong>${fmt.format(addBusinessDays(today, max))}</strong>`;
  }
  function paintEta() {
    const conf = readConfig() || {};
    document.querySelectorAll('[data-kc-eta]').forEach(el => {
      const min = Number(el.dataset.kcEtaMin || conf.etaMin || 7), max = Number(el.dataset.kcEtaMax || conf.etaMax || 11);
      if (!(min > 0 && max >= min)) return;
      el.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15" rx="1.5"/><path d="M3.5 10h17M8 3v4M16 3v4"/></svg><span>' + etaText(min, max) + '</span>';
      el.hidden = false;
    });
  }

  /* ---------- config + cart capture ---------- */
  function readConfig() {
    if (cfg) return cfg;
    const node = document.querySelector('[data-kc-config]');
    if (!node) return null;
    try { cfg = JSON.parse(node.textContent); } catch { cfg = null; }
    return cfg;
  }
  const isCartJson = data => data && Array.isArray(data.items) && typeof data.total_price === 'number' && 'item_count' in data;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async function (input, init) {
    const response = await nativeFetch(input, init);
    try {
      const url = typeof input === 'string' ? input : input?.url || '';
      if (response.ok && /\/cart(\.js|\/change(\.js)?|\/update(\.js)?|\/clear(\.js)?)(\?|$)/.test(url)) {
        response.clone().json().then(data => { if (isCartJson(data)) { cart = data; schedule(); } }).catch(() => {});
      }
    } catch {}
    return response;
  };

  /* ---------- helpers ---------- */
  const $ = (s, r = document) => r.querySelector(s);
  function counts(c) {
    let head = 0, caps = 0;
    for (const item of c.items) {
      if (item.handle === HEAD && !/duo/i.test(item.variant_title || '') && !item.selling_plan_allocation) head += item.quantity;
      if (item.handle === CAPSULE && !item.selling_plan_allocation) caps += item.quantity;
    }
    return { head, caps };
  }
  let frame;
  function schedule() { cancelAnimationFrame(frame); frame = requestAnimationFrame(render); }

  /* ---------- render ---------- */
  function render() {
    const drawer = $('#cart-dialog.kc');
    const conf = readConfig();
    if (!drawer || !conf || !cart) return;
    const { head, caps } = counts(cart);
    const total = cart.total_price, count = cart.item_count;
    const cures = conf.showCures !== false;

    $('[data-kc-count]', drawer).textContent = count ? `(${count})` : '';
    drawer.classList.toggle('kc-is-empty', !count);
    $('[data-kc-empty]', drawer).hidden = !!count;

    /* rewards ladder */
    const ship = total >= conf.freeShipping, six = cures && head > 0 && caps >= 1, year = cures && head > 0 && caps >= 3;
    const steps = cures ? 3 : 1;
    let fill;
    if (!ship) fill = (total / conf.freeShipping) * (100 / steps);
    else if (!cures) fill = 100;
    else fill = year ? 100 : six ? 66.67 : 33.34;
    $('[data-kc-fill]', drawer).style.width = Math.max(count ? 4 : 0, Math.min(100, fill)) + '%';
    const step = name => $(`[data-kc-step="${name}"]`, drawer);
    step('ship')?.classList.toggle('done', ship);
    step('six')?.classList.toggle('done', six);
    step('year')?.classList.toggle('done', year);
    const msg = $('[data-kc-reward-msg]', drawer);
    if (!count) msg.innerHTML = `Livraison suivie offerte en France dès <strong>${money(conf.freeShipping)}</strong>`;
    else if (!ship) msg.innerHTML = `Plus que <strong>${money(conf.freeShipping - total)}</strong> pour la livraison offerte`;
    else if (!cures || !head) msg.innerHTML = `<strong>Livraison suivie offerte</strong> en France ✓`;
    else if (!six) msg.innerHTML = `Livraison offerte ✓ · Ajoutez 1 recharge : <strong>−${money(conf.sixOff)}</strong>`;
    else if (!year) msg.innerHTML = `Encore <strong>${3 - caps} recharge${3 - caps > 1 ? 's' : ''}</strong> pour la Cure annuelle (−${money(conf.yearOff)})`;
    else msg.innerHTML = `<strong>Tous vos avantages sont débloqués</strong> · 12 mois de filtration ✓`;

    /* upsell */
    const box = $('[data-kc-upsell]', drawer), btn = $('[data-kc-upsell-btn]', drawer);
    let offer = null;
    if (count && head > 0 && conf.capsule?.available && cures) {
      if (caps === 0) offer = { qty: 1, kicker: 'Cure 6 mois · −' + money(conf.sixOff), title: 'Ajoutez une recharge', copy: `90 jours de filtration en plus pour ${money(conf.capsule.price - conf.sixOff)} au lieu de ${money(conf.capsule.price)}.`, label: '+ ' + money(conf.capsule.price - conf.sixOff) };
      else if (caps < 3) { const n = 3 - caps, extra = n * conf.capsule.price - conf.yearOff; offer = { qty: n, kicker: 'Cure annuelle · −' + money(conf.yearOff), title: `Passez à 12 mois de filtration`, copy: `Ajoutez ${n} recharge${n > 1 ? 's' : ''} : ${money(conf.yearOff)} déduits de la tête de douche${extra > 0 ? `, soit ${money(extra)} de plus seulement` : ''}.`, label: extra > 0 ? '+ ' + money(extra) : 'Ajouter' }; }
    } else if (count && head === 0 && caps > 0) {
      offer = { link: conf.headUrl, kicker: 'Pas encore équipé·e ?', title: 'La Tête de Douche KALIÉ', copy: `${money(conf.headPrice)} avec une capsule incluse, livraison offerte et essai 60 jours.`, label: 'Découvrir' };
    }
    box.hidden = !offer;
    if (offer) {
      $('[data-kc-upsell-kicker]', drawer).textContent = offer.kicker;
      $('[data-kc-upsell-title]', drawer).textContent = offer.title;
      $('[data-kc-upsell-copy]', drawer).textContent = offer.copy;
      $('[data-kc-upsell-img]', drawer).src = offer.link ? (window.KALIE_CONFIG?.steelImage || conf.capsuleImage) : conf.capsuleImage;
      btn.textContent = offer.label;
      btn.dataset.qty = offer.qty || '';
      btn.dataset.link = offer.link || '';
    }

    /* totals */
    const discount = cart.total_discount || Math.max(0, (cart.original_total_price || total) - total);
    $('[data-kc-subtotal]', drawer).textContent = money(total + discount);
    $('[data-kc-save-row]', drawer).hidden = !discount;
    $('[data-kc-save]', drawer).textContent = '−' + money(discount);
    $('[data-kc-ship]', drawer).innerHTML = ship ? '<span class="kc-free">Offerte</span>' : 'Calculée au paiement';
    $('#cart-total', drawer).textContent = money(total);
    $('[data-kc-btn-total]', drawer).textContent = count ? money(total) : '';

    enhanceRows(drawer);
    document.dispatchEvent(new CustomEvent('kalie:cart', { detail: { cart } }));
  }

  function enhanceRows(drawer) {
    if (!cart) return;
    const rows = drawer.querySelectorAll('#cart-items > .cart-item');
    rows.forEach((row, i) => {
      const item = cart.items[i];
      if (!item || row.dataset.kcKey === item.key + ':' + item.quantity + ':' + item.final_line_price) return;
      row.dataset.kcKey = item.key + ':' + item.quantity + ':' + item.final_line_price;
      const detail = row.querySelector(':scope > div');
      if (!detail) return;
      const [variant, price] = detail.querySelectorAll(':scope > p');
      if (variant) {
        variant.className = 'kc-variant';
        variant.textContent = (variant.textContent || '').replace(/\s*\/\s*Achat unique/i, '').replace(/^Default Title$/i, '');
        if (item.handle === CAPSULE && !variant.textContent) variant.textContent = 'Capsule KDF-55 · environ 90 jours';
      }
      if (price) {
        price.className = 'kc-price';
        price.replaceChildren();
        if (item.original_line_price > item.final_line_price) { const s = document.createElement('s'); s.textContent = money(item.original_line_price); price.append(s, ' '); }
        const b = document.createElement('b'); b.textContent = money(item.final_line_price); price.append(b);
      }
      detail.querySelector('.kc-badges')?.remove();
      const allocations = (item.line_level_discount_allocations || []).filter(a => a.amount > 0);
      if (allocations.length) {
        const wrap = document.createElement('div'); wrap.className = 'kc-badges';
        allocations.forEach(a => { const tag = document.createElement('span'); tag.textContent = (a.discount_application?.title || 'Remise').replace(/^KALIÉ\s*[—-]\s*/i, '').replace(/\s*·\s*sans abonnement/i, '') + ' · −' + money(a.amount); wrap.append(tag); });
        (price || detail).after(wrap);
      }
    });
  }

  /* ---------- upsell action ---------- */
  document.addEventListener('click', async event => {
    const btn = event.target.closest('[data-kc-upsell-btn]');
    if (!btn) return;
    if (btn.dataset.link) { location.assign(btn.dataset.link); return; }
    const conf = readConfig(), qty = Number(btn.dataset.qty);
    if (busy || !conf || !qty) return;
    busy = true; btn.disabled = true; btn.classList.add('is-busy');
    const status = $('#cart-status');
    try {
      const root = window.KALIE_CONFIG?.root || '/';
      const response = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: conf.capsule.id, quantity: qty }] }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Impossible d’ajouter la recharge pour le moment.');
      if (window.KALIE_CART?.refresh) await window.KALIE_CART.refresh();
      else { const fresh = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }); cart = await fresh.json(); render(); }
      if (status) status.textContent = qty > 1 ? `${qty} recharges ajoutées à votre panier.` : 'Recharge ajoutée à votre panier.';
    } catch (error) { if (status) status.textContent = error.message; }
    finally { busy = false; btn.disabled = false; btn.classList.remove('is-busy'); }
  });

  /* ---------- boot ---------- */
  function boot() {
    const tpl = document.getElementById('kc-trust-tpl');
    if (tpl) document.querySelectorAll('.pdp-perks').forEach(anchor => { if (!anchor.nextElementSibling?.classList.contains('kc-strip')) anchor.after(tpl.content.cloneNode(true)); });
    paintEta();
    const items = $('#cart-dialog.kc #cart-items');
    if (items) new MutationObserver(() => { const d = $('#cart-dialog.kc'); if (d) enhanceRows(d); }).observe(items, { childList: true });
    const dialog = $('#cart-dialog.kc');
    if (dialog && !cart) {
      // first paint even if the main script has not fetched the cart yet
      nativeFetch((window.KALIE_CONFIG?.root || '/') + 'cart.js', { headers: { Accept: 'application/json' } }).then(r => r.json()).then(data => { if (isCartJson(data) && !cart) { cart = data; schedule(); } }).catch(() => {});
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
